import { useStore } from '@/store';
import { InternalState } from '@/store/modules';
import { Client, User } from '@/types/app';
import { cloneDeep } from 'lodash';
import { computed, Ref, watch, WritableComputedRef } from 'vue';

/**
 * Creates a new array with unique ID's. ID's are handled on a first-come-first-serve basis.
 * @param arr Array to remove duplicate ID's from
 * @returns A new array with duplicates removed.
 */
export const removeDuplicateIds = <T extends { id: string }>(arr: Array<T>): Array<T> => {
  const foundIds = new Set<string>();

  return arr.filter(obj => {
    if (foundIds.has(obj.id)) return false;

    foundIds.add(obj.id);
    return true;
  });
};

/**
 * Creates an object of writable computed methods with get methods mapped to store state and set methods mapped to store actions.
 * @param state The object within the state that you would like to change the properties of.
 * @param storeSetter The action to call from the store to update the properties of your state object.
 * @param keys The keys from the state object you would like to create writable computed store methods for. If left empty the function will return all properties.
 * @returns An object that holds the writable computed methods, keyed using the state object keys.
 */
export const createWritableComputedStoreMethodsFromStoreObject = <S extends Record<string, unknown>>(
  state: S,
  storeSetter: (newValue: Record<keyof S, S[keyof S]>) => void,
  keys: Array<keyof S>
): Record<keyof S, WritableComputedRef<S[keyof S]>> => {
  const filteredKeys = keys.filter(key => {
    const stateKeys = Object.keys(state);
    return stateKeys.includes(key as string);
  });

  const writableComputedStoreMethodEntries = filteredKeys.map(key => {
    return [
      key,
      computed({
        get: () => cloneDeep(state[key] as S[keyof S]),
        set: value => storeSetter({ [key]: value } as Record<keyof S, S[keyof S]>)
      })
    ] as [keyof S, WritableComputedRef<S[keyof S]>];
  });
  const writableComputedStoreMethods = Object.fromEntries(writableComputedStoreMethodEntries);

  return writableComputedStoreMethods as Record<keyof S, WritableComputedRef<S[keyof S]>>;
};

export const useSharedInternalState = <PropName extends keyof InternalState>(key: PropName) => {
  const store = useStore();
  return computed<InternalState[PropName]>({
    get: () => store.getters.internal.get(key) as InternalState[PropName],
    set: value => store.dispatch.internal.update([key, value])
  });
};

// Probably not the right spot for this...
export const useSelectSubmittedClient = (clientRef: Ref<Client> | WritableComputedRef<Client>) => (newClient: Client) => {
  const store = useStore();
  const unsubscribe = watch(
    () => store.getters.clients.get,
    storeClients => {
      const newClientInStore = storeClients.find(
        storeClient =>
          storeClient.name === newClient.name &&
          storeClient.phone === newClient.phone &&
          storeClient.email === newClient.email
      );

      store.dispatch.internal.update(['isLoading', true]);

      if (newClientInStore?.lastUpdatedOn?.user === (store.getters.users.getCurrentUser as User | undefined)?.id) return;

      store.dispatch.internal.update(['isLoading', false]);
      if (newClientInStore) {
        // eslint-disable-next-line no-param-reassign
        clientRef.value = newClientInStore;
      }
      unsubscribe();
    }
  );
};

export const useStoreGetterOrDefault = <T extends Record<string, unknown>>(getter: () => T | undefined, defaultValue: T) =>
  computed<T & { IS_DEFAULT?: boolean }>(() => getter() ?? { ...defaultValue, IS_DEFAULT: true });
